import React, { useCallback, useContext, useEffect } from 'react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { Button } from '@fluentui/react-components';
import { FORM_EVENTS, FORM_STATES, useFormService } from '@bingads-webui-react/form-core';

interface SaveButtonProps {
  buttonText?: string;
  disabled?: boolean;
  appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
}
export const SaveButton: React.FC<SaveButtonProps> = ({ buttonText, disabled, appearance }) => {
  const i18n = useContext(I18nContext) as any;

  const { state, send } = useFormService();
  const isBlocking = state.matches(FORM_STATES.FETCHING) || state.matches(FORM_STATES.LOADING) || state.matches(FORM_STATES.SAVING);

  const onSaveClicked = useCallback(() => {
    send(FORM_EVENTS.SAVE);
  }, [send]);

  return (
    <Button
      appearance={appearance || 'primary'}
      disabled={disabled || isBlocking}
      onClick={onSaveClicked}
      className="form-save-button"
    >
      {buttonText || i18n.getString(_TL_('Save'))}
    </Button>
  );
};
SaveButton.defaultProps = {
  buttonText: undefined,
  disabled: false,
};

interface CancelButtonProps {
  buttonText?: string;
  appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
}
export const CancelButton: React.FC<CancelButtonProps> = ({ buttonText, appearance }) => {
  const i18n = useContext(I18nContext) as any;

  const { state, send } = useFormService();
  const isBlocking = state.matches(FORM_STATES.FETCHING) || state.matches(FORM_STATES.LOADING) || state.matches(FORM_STATES.SAVING);

  const onCancelClicked = useCallback(() => {
    send(FORM_EVENTS.CANCEL);
  }, [send]);

  return (
    <Button
      appearance={appearance || 'secondary'}
      disabled={isBlocking}
      onClick={onCancelClicked}
      className="form-cancel-button"
    >
      {buttonText || i18n.getString(_TL_('Cancel'))}
    </Button>
  );
};
CancelButton.defaultProps = {
  buttonText: undefined,
};

interface PreviewButtonProps {
  buttonText?: string;
  disabled?: boolean;
  appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
}
export const PreviewButton: React.FC<PreviewButtonProps> = ({ buttonText, disabled, appearance }) => {
  const i18n = useContext(I18nContext) as any;

  const { state, send } = useFormService();
  const isBlocking = state.matches(FORM_STATES.FETCHING) || state.matches(FORM_STATES.LOADING) || state.matches(FORM_STATES.SAVING);

  // Clean up isPreview when save completes (success or failure)
  useEffect(() => {
    const isSavingCompleted = state.matches(FORM_STATES.SAVING_SUCCEEDED) || state.matches(FORM_STATES.SAVING_FAILED);

    if (isSavingCompleted && state.context.isPreview) {
      // Clear isPreview after save completes
      send({ type: FORM_EVENTS.UPDATE_CONTEXT, context: { isPreview: undefined } });
    }
  }, [state.value, state.context.isPreview, send, state]);

  const onPreviewClicked = useCallback(() => {
    send({ type: FORM_EVENTS.UPDATE_CONTEXT, context: { isPreview: true } });
    send(FORM_EVENTS.SAVE);
  }, [send]);

  return (
    <Button
      appearance={appearance || 'secondary'}
      disabled={disabled || isBlocking}
      onClick={onPreviewClicked}
      className="form-preview-button"
    >
      {buttonText || i18n.getString(_TL_('Preview'))}
    </Button>
  );
};
PreviewButton.defaultProps = {
  buttonText: undefined,
  disabled: false,
};
